import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { TableDateRangeFilter } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { List } from 'components/common/ListWrapper/list';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { useRiskDensityLogic } from 'components/widgets/RiskDensity/RiskDensity.logic';
import classes from 'components/widgets/RiskDensity/RiskDensity.module.scss';
import {
    SelectByOptionsValue,
    resolutionOptions,
    selectByOptions,
} from 'components/widgets/RiskDensity/RiskDensity.utils';
import CriticalAnalysisHeatMap from 'components/widgets/RiskDensity/components/CriticalAnalysisHeatMap/CriticalAnalysisHeatMap';

import { useRiskDensityProvider } from './RiskDensity.provider';

const { id, componentKey, endpoint } = widgetsConfigurations.riskHeatMap;

export const RiskDensity = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.riskHeatMap.title,
    setIsPayloadReady,
}: IComponentProps) => {
    // provider
    const {
        ui,
        api,
        setStartProjectDate,
        setEndProjectDate,
        setDateRange,
        setDateRangeOption,
        setRiskDensityBy,
        setResolution,
        setSelectBy,
    } = useRiskDensityProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        widgetViewMode,
        componentKey,
    });

    // logic
    const { logic } = useRiskDensityLogic({
        ui,
        api,
        setStartProjectDate,
        setEndProjectDate,
        setDateRange,
        setDateRangeOption,
        setRiskDensityBy,
        setResolution,
        setSelectBy,
        widgetViewMode,
        setIsPayloadReady,
    });

    // baseWidget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.riskDensityBy.payload,
        widgetEndpoint: endpoint[ui.state.selectBy],
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle
                        title={editableTitle}
                        setTitle={setEditableTitle}
                        widgetViewMode={widgetViewMode}
                        postfix={logic.selectByValue?.label}
                    />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    <div className={classes.titleComponents} key={'filterCriticalAnalysisHeatMap'}>
                        {WidgetViewModeUtils.isPinboardView(widgetViewMode) && <h3>{logic.selectByValue?.label}</h3>}
                        {WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                            <>
                                <Select<ISelectOption<SelectByOptionsValue>>
                                    id={'filterCriticalAnalysisHeatMap'}
                                    value={logic.selectByValue}
                                    options={selectByOptions}
                                    onChange={logic.handleSelectBy}
                                    key={'filterCriticalAnalysisHeatMap-Key'}
                                />
                                <div className={classes.dateRangeWrapper}>
                                    <div>{FilterLabels.dateRange}</div>
                                    {ui.state.startProjectDate && ui.state.endProjectDate && (
                                        <TableDateRangeFilter
                                            onChange={logic.handleDateRangeSelection}
                                            minDate={ui.state.startProjectDate}
                                            maxDate={ui.state.endProjectDate}
                                            value={ui.state.dateRange}
                                            isDisabled={ui.isInPinboard}
                                            dateRangeOption={ui.state.dateRangeOption}
                                            setDateRangeOption={setDateRangeOption}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>,
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <div className={classes.resolutionWrapper} key={'resolution'}>
                            <div className={classes.title}>Resolution:</div>
                            <List<boolean>
                                onChange={logic.handleResolutionFilter}
                                options={resolutionOptions}
                                value={logic.resolutionValue}
                            />
                        </div>
                    ),
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidgetToPinboard'}
                        title={title}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.riskDensityBy.payload}
                        widgetEndpoint={endpoint[ui.state.selectBy]}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.riskDensityBy.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                {WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                    <div className={classes.topSection}>
                        <div>
                            <span>Click any month to filter the activities table below.</span>
                        </div>
                    </div>
                )}
                <CriticalAnalysisHeatMap
                    data={logic.serialized}
                    handleClick={logic.setDateOnDelayDriver}
                    resolution={ui.state.resolution}
                    noData={
                        !api.riskDensityBy.instance.isFetching &&
                        api.riskDensityBy.instance.data !== null &&
                        logic.serialized.length === 0
                    }
                    isFetching={api.riskDensityBy.instance.isFetching}
                />
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
